/**
 * ⌘K / Ctrl+K command palette (UI review §2.1): jump to pages, settings, people, buildings and chats, or fall back to
 * a full message search. Only lists what the person can open (same rules as the sidebar and the Settings hub).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RoomIcon } from '@/features/rooms/RoomIcon';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Building2, CircleHelp, Clock, CornerDownLeft, Hash, Lock, MessageSquare, Search, Settings, User, ListTodo, Sparkles } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { conversationName, useConversations, useMembers } from '@/lib/hooks';
import { useNavAccess } from '@/lib/navAccess';
import { allNavItems } from '@/lib/navModel';
import { usePermissions } from '@/lib/permissions';
import { useUi } from '@/lib/store';
import { holdsAnywhere } from '@/lib/navAccess';
import { useBuildings } from '@/features/buildings/common';
import { searchSections, sectionPath, visibleSections, GROUPS } from '@/features/settings/registry';
import { useHelpSearch } from '@/features/help/HelpSearch';
import { areaTitle, helpHref } from '@/features/help/data';
import { useDebounced } from '@/lib/useDebounced';
import { channelLabel, useChannelSuggest } from './PeopleSuggest';
import { isModuleOn } from '@tradeoutlet/shared';

interface Hit { id: string; kind: 'Page' | 'Setting' | 'Help' | 'Person' | 'Building' | 'Chat' | 'Channel' | 'Action' | 'Search' | 'Assistant'; label: string; sub?: string; icon: React.ReactNode; run: () => void }

const match = (q: string, ...hay: (string | null | undefined)[]) => {
  const h = hay.filter(Boolean).join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every(w => h.includes(w));
};

export function CommandPalette() {
  const open = useUi(s => s.paletteOpen);
  const setOpen = useUi(s => s.setPaletteOpen);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(!useUi.getState().paletteOpen); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);
  return open ? <Palette onClose={() => setOpen(false)} /> : null;
}

function Palette({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const { userId } = useAuth();
  const { can } = useNavAccess();
  const mine = usePermissions().data;
  const openPerson = useUi(s => s.openPerson);
  const openAssistant = useUi(s => s.openAssistant);
  const canAssistant = Boolean(mine && isModuleOn(mine.modules_off, 'assistant') && (mine.workspace.includes('ai.assistant.use') || mine.workspace.includes('assistant.use')));
  const { members } = useMembers();
  const convs = useConversations().data?.conversations ?? [];
  const buildings = useBuildings().data?.buildings ?? [];
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const prevFocus = useRef<HTMLElement | null>(document.activeElement as HTMLElement | null);
  useEffect(() => { input.current?.focus(); const prev = prevFocus.current; return () => prev?.focus?.(); }, []);
  const go = (to: string) => { onClose(); nav(to); };
  // "Help: …" results from the User Guide (the index chunk loads on the first keystroke).
  const helpQ = useDebounced(q, 100);
  const help = useHelpSearch(helpQ, 4).hits;
  // t645u: channels from the server search too, so public channels you're not in can be found (and previewed, then joined).
  const found = useChannelSuggest(q, true, 5);

  const hits = useMemo<Hit[]>(() => {
    const t = q.trim();
    const pages = allNavItems().filter(p => can(p.to)).filter((p, idx, arr) => arr.findIndex(x => x.to === p.to) === idx)
      .filter(p => !t || match(t, p.label, ...(p.keywords ?? [])))
      .map<Hit>(p => ({ id: `p:${p.to}`, kind: 'Page', label: p.label, icon: <p.icon className="h-4 w-4" />, run: () => go(p.to) }));
    const newTodo = (title: string): Hit => ({ id: 'a:todo', kind: 'Action', label: title ? `New private task: “${title}”` : 'New private task', sub: 'Only you can see it', icon: <ListTodo className="h-4 w-4" />, run: () => go(`/tasks?new=private&title=${encodeURIComponent(title)}`) });
    // #52: hand the text to the assistant (actions for ai.assistant.use, otherwise the #8 workplace Q&A).
    const ask: Hit[] = canAssistant ? [{ id: 'a:assistant', kind: 'Assistant', label: t ? `Ask the assistant: “${t}”` : 'Ask the assistant', sub: 'Answers from the Help Centre, or sets things up for you to check', icon: <Sparkles className="h-4 w-4" />,
      run: () => { onClose(); openAssistant(t || undefined, 'panel'); } }] : [];
    // t369u: no private-task action with Tasks off, no ad-hoc meeting rooms with Calls off.
    const off = mine?.modules_off;
    const todos = isModuleOn(off, 'tasks');
    if (!t) return [...pages.slice(0, 7), ...(todos ? [newTodo('')] : []), ...ask];
    // Ad-hoc meeting rooms (#54): start one, or find a closed one.
    const rooms: Hit[] = isModuleOn(off, 'calls') && match(t, 'new ad-hoc meeting room', 'ad-hoc meeting room', 'closed ad-hoc meeting rooms', 'room', 'call') ? [
      ...(mine && holdsAnywhere(mine, 'chat.room.create') ? [{ id: 'a:room', kind: 'Action', label: 'New ad-hoc meeting room', sub: 'A call and chat with people from any team', icon: <RoomIcon />, run: () => { onClose(); useUi.getState().setNewRoomOpen(true); } } as Hit] : []),
      { id: 'p:/meeting-rooms/closed', kind: 'Page', label: 'Closed ad-hoc meeting rooms', icon: <RoomIcon />, run: () => go('/meeting-rooms/closed') },
    ] : [];
    // t622u: your scheduled chat messages.
    if (match(t, 'scheduled messages', 'schedule', 'send later')) rooms.push({ id: 'p:/chat/scheduled', kind: 'Page', label: 'Scheduled messages', icon: <Clock className="h-4 w-4" />, run: () => go('/chat/scheduled') });
    const wantsTodo = todos && match(t, 'new private task', 'private', 'new to-do', 'todo', 'to do', 'remind');
    const settings = searchSections(visibleSections(mine), t).slice(0, 6).map<Hit>(s => ({
      id: `s:${s.group}/${s.item}`, kind: 'Setting', label: s.title, sub: GROUPS.find(g => g.id === s.group)?.title, icon: <Settings className="h-4 w-4" />, run: () => go(s.href ?? sectionPath(s)),
    }));
    const people = members.filter(m => match(t, m.display_name, m.email, m.job_title)).slice(0, 5).map<Hit>(m => ({
      id: `u:${m.user_id}`, kind: 'Person', label: m.display_name, sub: m.job_title ?? m.email ?? undefined, icon: <User className="h-4 w-4" />, run: () => { onClose(); openPerson(m.user_id); },
    }));
    const blds = buildings.filter(b => match(t, b.name, b.code, b.address)).slice(0, 4).map<Hit>(b => ({
      id: `b:${b.id}`, kind: 'Building', label: b.name, sub: b.address ?? undefined, icon: <Building2 className="h-4 w-4" />, run: () => go(`/buildings/${b.id}`),
    }));
    const chats = convs.filter(c => match(t, conversationName(c, userId), c.topic)).slice(0, 5).map<Hit>(c => ({
      id: `c:${c.id}`, kind: 'Chat', label: conversationName(c, userId), sub: c.kind === 'room' ? 'Ad-hoc meeting room' : undefined, icon: c.kind === 'channel' ? (c.is_private ? <Lock className="h-4 w-4" /> : <Hash className="h-4 w-4" />) : c.kind === 'room' ? <RoomIcon /> : <MessageSquare className="h-4 w-4" />, run: () => go(`/c/${c.id}`),
    }));
    const mineIds = new Set(convs.map(c => c.id));
    const joinable = found.filter(c => !mineIds.has(c.id)).map<Hit>(c => ({
      id: `c:${c.id}`, kind: 'Channel', label: channelLabel(c), sub: c.is_member ? c.description ?? undefined : ['Not a member · open to preview and join', c.description].filter(Boolean).join(' · '),
      icon: c.is_private ? <Lock className="h-4 w-4" /> : <Hash className="h-4 w-4" />, run: () => go(`/c/${c.id}`),
    }));
    const search: Hit = { id: 'search', kind: 'Search', label: `Search everything for “${t}”`, icon: <Search className="h-4 w-4" />, run: () => go(`/search?q=${encodeURIComponent(t)}`) };
    const guides = help.map<Hit>(h => ({
      id: `h:${h.key}#${h.anchor ?? ''}`, kind: 'Help', label: `Help: ${h.title}`, sub: `${areaTitle(h.area)}${h.heading ? ` › ${h.heading}` : ''}`, icon: <CircleHelp className="h-4 w-4" />, run: () => go(helpHref(h.key, h.anchor)),
    }));
    return wantsTodo ? [newTodo(''), ...pages.slice(0, 5), ...rooms, ...settings, ...guides, ...people, ...blds, ...chats, ...joinable, search]
      : [...pages.slice(0, 5), ...rooms, ...settings, ...guides, ...people, ...blds, ...chats, ...joinable, ...ask, ...(todos ? [newTodo(t)] : []), search];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- go/openPerson are stable enough for a short-lived dialog
  }, [q, mine, members, convs, buildings, userId, help, canAssistant, found]);
  useEffect(() => setI(0), [q]);
  const active = hits[Math.min(i, hits.length - 1)];

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setI(x => (x + 1) % Math.max(hits.length, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setI(x => (x - 1 + hits.length) % Math.max(hits.length, 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); active?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'Tab') e.preventDefault(); // focus stays in the box; arrows move the selection
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[12vh]" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Go to" className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-900" data-testid="command-palette">
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 dark:border-slate-800">
          <Search className="h-4 w-4 text-slate-500" />
          <input ref={input} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder="Go to a page, setting, help guide, person, building, chat or channel…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-slate-500"
            role="combobox" aria-expanded="true" aria-controls="palette-list" aria-activedescendant={active ? `palette-${active.id}` : undefined} aria-autocomplete="list" />
          <kbd className="rounded border border-slate-300 px-1.5 text-[10px] text-slate-500 dark:border-slate-700">Esc</kbd>
        </div>
        <ul id="palette-list" role="listbox" aria-label="Results" className="scroll-thin max-h-[60vh] overflow-y-auto py-1">
          {hits.map((h, idx) => (
            <li key={h.id} id={`palette-${h.id}`} role="option" aria-selected={h === active}
              onMouseMove={() => setI(idx)} onClick={() => h.run()}
              className={clsx('flex cursor-pointer items-center gap-3 px-3 py-2 text-sm', h === active ? 'bg-slate-100 dark:bg-slate-800' : '')}>
              <span className="text-slate-500">{h.icon}</span>
              <span className="min-w-0 flex-1"><span className="block truncate">{h.label}</span>{h.sub && <span className="block truncate text-xs text-slate-500">{h.sub}</span>}</span>
              <span className="text-[11px] uppercase tracking-wide text-slate-500">{h.kind}</span>
              {h === active && <CornerDownLeft className="h-3.5 w-3.5 text-slate-500" />}
            </li>
          ))}
          {!hits.length && <li className="px-3 py-6 text-center text-sm text-slate-500">Nothing found.</li>}
        </ul>
      </div>
    </div>
  );
}
